import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

dayjs.extend(customParseFormat);

export interface ExportOptions {
	fileName?: string;
	sheetName?: string;
	columns?: { key: string; header?: string }[];
}

export const exportToExcel = async (
	data: any[],
	options: ExportOptions = {},
) => {
	if (!data || !data.length) {
		console.warn("No data to export");
		return;
	}

	const {
		fileName = "ExportFile.xlsx",
		sheetName = "Sheet1",
		columns,
	} = options;

	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(sheetName);

	const autoColumns =
		columns?.map((c: any) => ({
			key: c.accessorKey ?? c.key,
			header: c.header ?? c.accessorKey ?? c.key,
		})) ||
		Object.keys(data[0]).map((key) => ({
			key,
			header: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
		}));

	// Filter out Actions/Action columns
	const filteredColumns = autoColumns.filter(
		(col) => col.header !== "Actions" && col.header !== "Action",
	);

	worksheet.columns = filteredColumns.map((col) => ({
		header: col.header,
		key: col.key,
		width: 20, // Default width
	}));

	data.forEach((row, index) => {
		const rowData: Record<string, any> = {};

		filteredColumns.forEach((col) => {
			const headerName = col.header ?? col.key;

			if (headerName === "S.No.") {
				rowData[col.key] = index + 1;
				return;
			}

			let value = row[col.key];

			if (col.key?.toLowerCase().includes("date") && value) {
				value = dayjs(value).format("DD-MM-YYYY");
			}

			if (col.key?.toLowerCase().includes("time") && value) {
				value = dayjs(value, ["HH:mm", "HH:mm:ss"]).format("hh:mm A");
			}
			if (
				col.key?.toLowerCase().includes("created_on") ||
				col.key?.toLowerCase().includes("updatedOn") ||
				col.key?.toLowerCase().includes("created_at") ||
				col.key?.toLowerCase().includes("updated_at") ||
				col.key?.toLowerCase().includes("created_date") ||
				col.key?.toLowerCase().includes("updated_date") ||
				col.key?.toLowerCase().includes("created_on")
			) {
				value = dayjs(value).isValid()
					? dayjs(value).format("DD-MM-YYYY hh:mm A")
					: value;
			}

			if (col.key?.toLowerCase().includes("mobile") && value) {
				value = String(value);
			}

			if (col.key?.toLowerCase().includes("is_active") && value) {
				value = value === 1 ? "Active" : "Inactive";
			}

			if (col.key?.toLowerCase().includes("status") && value) {
				value = row.status_name ?? row[col.key];
			}

			rowData[col.key] = value ?? "";
		});

		worksheet.addRow(rowData);
	});

	// Styling the header
	worksheet.getRow(1).font = { bold: true };
	worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };

	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	saveAs(blob, fileName);
};
